import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  countersApi,
  type CounterRow,
  type CounterTable,
  type CustomField,
} from '@/api/queries/counters';
import { Button, Icon, Input, Table, useToast, type TableColumn } from '@/components/ui';

import { AddCounterRowModal } from './AddCounterRowModal';
import { AddLanguageModal } from './AddLanguageModal';
import { CounterSchemaModal } from './CounterSchemaModal';
import { EditCounterTableModal } from './EditCounterTableModal';
import { ImportCounterCsvModal } from './ImportCounterCsvModal';

interface Props {
  tableKey: string;
}

export function CounterTableDetail({ tableKey }: Props) {
  const search = useSearch({ from: '/counters/$key' });
  const navigate = useNavigate({ from: '/counters/$key' });
  const { data: table, isPending, error } = countersApi.useTable(tableKey);
  const acceptTranslations = countersApi.useAcceptTranslations();
  const deleteRow = countersApi.useDeleteRow();
  const deleteTable = countersApi.useDeleteTable();
  const toast = useToast();
  const [showSchema, setShowSchema] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const handleDelete = () => {
    if (!table) return;
    if (!confirm(`Delete counter table "${table.name}" (counter.${table.key})? This can't be undone.`)) return;
    deleteTable.mutate(table.key, {
      onSuccess: () => {
        toast.show({ severity: 'success', title: `Deleted ${table.name}` });
        navigate({ to: '/counters', search: { view: 'table' } });
      },
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: err instanceof Error ? err.message : 'Failed to delete counter',
        }),
    });
  };

  const activeLang = search.lang ?? table?.defaultLang ?? 'cs';
  const setLang = (lang: string) =>
    navigate({ search: (prev) => ({ ...prev, lang }) });
  const setQuery = (q: string) =>
    navigate({ search: (prev) => ({ ...prev, q: q || undefined }) });
  const setMissingOnly = (v: boolean) =>
    navigate({ search: (prev) => ({ ...prev, missingOnly: v || undefined }) });

  const filteredRows = useMemo(() => {
    if (!table) return [];
    const q = (search.q ?? '').trim().toLowerCase();
    return table.rows.filter((row) => {
      if (q) {
        const inKey = row.key.toLowerCase().includes(q);
        const inVal = Object.values(row.value ?? {}).some((v) =>
          v?.toLowerCase().includes(q),
        );
        if (!inKey && !inVal) return false;
      }
      if (search.missingOnly && !isRowMissingLang(row, activeLang, table.defaultLang)) {
        return false;
      }
      return true;
    });
  }, [table, search.q, search.missingOnly, activeLang]);

  if (error) {
    return (
      <div className="empty-state">
        <Icon name="alert" size={28} />
        <p>Failed to load counter table.</p>
      </div>
    );
  }
  if (!table) {
    return <div className="empty-state">{isPending ? 'Loading…' : 'Not found'}</div>;
  }

  const columns: TableColumn<CounterRow>[] = [
    {
      id: 'key',
      header: 'Key',
      width: 160,
      cell: (row) => <code style={{ fontSize: 12 }}>{row.key}</code>,
    },
    {
      id: 'value',
      header: 'Value',
      cell: (row) => {
        const missing = isRowMissingLang(row, activeLang, table.defaultLang);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{row.value?.[activeLang] || row.value?.[table.defaultLang] || ''}</span>
            {missing && (
              <span title="Missing translation" style={{ color: 'var(--amber, #d97706)' }}>
                <Icon name="alert" size={12} />
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      cell: (row) => (
        <span style={{ color: 'var(--text-2)', fontSize: 13 }}>
          {row.description?.[activeLang] || row.description?.[table.defaultLang] || '—'}
        </span>
      ),
    },
    {
      id: 'parent',
      header: 'Parent',
      width: 110,
      cell: (row) => row.parent || <span style={{ color: 'var(--text-4)' }}>—</span>,
    },
    ...(table.customFields ?? []).map<TableColumn<CounterRow>>((cf) => ({
      id: `custom:${cf.key}`,
      header: cf.label,
      cell: (row) => {
        const v = row.custom?.[cf.key];
        if (cf.i18n && v && typeof v === 'object') {
          const obj = v as Record<string, unknown>;
          return String(obj[activeLang] ?? obj[table.defaultLang] ?? '');
        }
        return v === undefined || v === null ? '—' : String(v);
      },
    })),
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{table.name}</h1>
          <code style={{ color: 'var(--text-3)' }}>counter.{table.key}</code>
          {table.description && (
            <p className="page-sub" style={{ marginTop: 6 }}>
              {table.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" leftIcon={<Icon name="edit" size={14} />} onClick={() => setShowEdit(true)}>
            Edit
          </Button>
          <Button variant="ghost" leftIcon={<Icon name="languages" size={14} />} onClick={() => setShowLangs(true)}>
            Add language
          </Button>
          <Button variant="ghost" leftIcon={<Icon name="columns" size={14} />} onClick={() => setShowSchema(true)}>
            Schema
          </Button>
          <Button
            variant="ghost"
            className="danger"
            leftIcon={<Icon name="trash" size={14} />}
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setShowAddRow(true)}
          >
            Add row
          </Button>
        </div>
      </div>

      {table.pendingReview && (
        <ReviewTranslationsBanner
          lang={table.pendingReview}
          onAccept={async () => {
            try {
              await acceptTranslations.mutateAsync({ key: tableKey, lang: table.pendingReview! });
              toast.show({
                severity: 'success',
                title: `Accepted ${table.pendingReview} translations`,
              });
            } catch (err) {
              toast.show({
                severity: 'error',
                title: err instanceof Error ? err.message : 'Failed to accept',
              });
            }
          }}
        />
      )}

      <LanguageTabs
        table={table}
        activeLang={activeLang}
        onSelect={setLang}
      />

      <div className="toolbar" style={{ marginTop: 12, marginBottom: 10 }}>
        <Input
          placeholder="Filter rows"
          value={search.q ?? ''}
          onChange={(e) => setQuery(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 320 }}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={search.missingOnly ?? false}
            onChange={(e) => setMissingOnly(e.currentTarget.checked)}
          />
          Missing translation
        </label>
        <button
          type="button"
          className="ds-btn ds-btn-ghost ds-btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowImport(true)}
        >
          <Icon name="upload" size={12} /> Import CSV
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-ghost ds-btn-sm"
          onClick={async () => {
            try {
              await countersApi.exportCSV(table.key, 'all');
            } catch (err) {
              toast.show({
                severity: 'error',
                title: err instanceof Error ? err.message : 'Export failed',
              });
            }
          }}
        >
          <Icon name="download" size={12} /> Export CSV
        </button>
      </div>

      <Table<CounterRow>
        columns={columns}
        rows={filteredRows}
        rowKey={(r) => r.key}
        loading={isPending}
        emptyMessage="No rows match."
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            aria-label="Delete row"
            onClick={async () => {
              if (!confirm(`Delete row ${row.key}?`)) return;
              try {
                await deleteRow.mutateAsync({ key: tableKey, rowKey: row.key });
                toast.show({ severity: 'success', title: `Deleted ${row.key}` });
              } catch (err) {
                toast.show({
                  severity: 'error',
                  title: err instanceof Error ? err.message : 'Delete failed',
                });
              }
            }}
          >
            <Icon name="trash" size={12} />
          </Button>
        )}
      />

      {showEdit && (
        <EditCounterTableModal
          table={{ key: table.key, name: table.name, description: table.description }}
          onClose={() => setShowEdit(false)}
          onRenamed={(k) => navigate({ to: '/counters/$key', params: { key: k } })}
        />
      )}
      {showSchema && (
        <CounterSchemaModal
          table={table}
          onClose={() => setShowSchema(false)}
        />
      )}
      {showAddRow && (
        <AddCounterRowModal
          table={table}
          activeLang={activeLang}
          onClose={() => setShowAddRow(false)}
        />
      )}
      {showLangs && (
        <AddLanguageModal table={table} onClose={() => setShowLangs(false)} />
      )}
      {showImport && (
        <ImportCounterCsvModal table={table} onClose={() => setShowImport(false)} />
      )}
    </div>
  );
}

function LanguageTabs({
  table,
  activeLang,
  onSelect,
}: {
  table: CounterTable;
  activeLang: string;
  onSelect: (lang: string) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        borderBottom: '1px solid var(--border)',
        marginTop: 12,
      }}
    >
      {table.langs.map((lang) => {
        const active = lang === activeLang;
        return (
          <button
            key={lang}
            type="button"
            onClick={() => onSelect(lang)}
            style={{
              padding: '8px 14px',
              border: 'none',
              borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
              background: 'transparent',
              color: active ? 'var(--text-1)' : 'var(--text-3)',
              fontWeight: active ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {lang}
            {lang === table.defaultLang && (
              <span style={{ color: 'var(--text-4)', marginLeft: 4, fontSize: 11 }}>· default</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ReviewTranslationsBanner({
  lang,
  onAccept,
}: {
  lang: string;
  onAccept: () => Promise<void>;
}) {
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        border: '1px solid var(--amber-soft, #fde68a)',
        background: 'var(--amber-bg, #fef3c7)',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Icon name="sparkles" size={16} />
      <div style={{ flex: 1 }}>
        <strong>Review {lang} translations.</strong>{' '}
        <span style={{ color: 'var(--text-2)' }}>
          Auto-generated drafts are waiting. Review the per-row values, then accept to promote.
        </span>
      </div>
      <Button variant="primary" size="sm" onClick={onAccept}>
        Accept {lang}
      </Button>
    </div>
  );
}

function isRowMissingLang(row: CounterRow, lang: string, defaultLang: string) {
  if (lang === defaultLang) return false;
  const v = row.value?.[lang];
  if (!v) return true;
  return v === row.value?.[defaultLang];
}

function getCustomFieldDefault(cf: CustomField) {
  switch (cf.type) {
    case 'boolean':
      return false;
    case 'number':
      return 0;
    default:
      return '';
  }
}
export { getCustomFieldDefault };
